<script setup>
import { ref, watch, provide, computed, markRaw } from 'vue'
import { useRoute, useRouter } from 'vue-router'

import { callbacksStore } from '@/composables/useSidePanel'

import { parseHash, buildHash } from './hashParser'
import PanelLevelProvider from './PanelLevelProvider.vue'
import { getPanelInfo, calculatePanelSize } from './registry'

const route = useRoute()
const router = useRouter()
const panelStack = ref([])

// Provide context reativo para componentes filhos do manager
// (apenas panelContexts ainda usa provide pois é computado aqui)

// Computed para context de cada nível (disponível via provide)
const panelContexts = computed(() => {
  const contexts = {}

  panelStack.value.forEach((panel, index) => {
    contexts[panel.level] = {
      own: {
        level: panel.level,
        name: panel.name,
        props: panel.props,
        context: panel.context,
      },
      parent:
        index > 0
          ? {
              level: panelStack.value[index - 1].level,
              name: panelStack.value[index - 1].name,
              props: panelStack.value[index - 1].props,
              context: panelStack.value[index - 1].context,
            }
          : null,
      root: panelStack.value[0]
        ? {
            level: panelStack.value[0].level,
            name: panelStack.value[0].name,
            props: panelStack.value[0].props,
            context: panelStack.value[0].context,
          }
        : null,
    }
  })

  return contexts
})

// Provide context reativo
provide('sidePanelContexts', panelContexts)

// Observa mudanças no hash da URL
watch(
  () => route.hash,
  async newHash => {
    await syncStackFromHash(newHash)
  },
  { immediate: true },
)

/**
 * Sincroniza stack com hash da URL
 */
async function syncStackFromHash(hash) {
  const levels = parseHash(hash)

  // Se não há níveis, limpar stack
  if (levels.length === 0) {
    panelStack.value.splice(0)
    return
  }

  // Se estamos REMOVENDO painéis (fechando), usar splice
  if (levels.length < panelStack.value.length) {
    panelStack.value.splice(levels.length)
    return
  }

  // Construir novo stack reutilizando painéis existentes
  const newStack = []

  for (const levelData of levels) {
    // Tentar reutilizar painel existente do mesmo nível
    const existingPanel = panelStack.value.find(
      p => p.level === levelData.level && p.name === levelData.name,
    )

    if (existingPanel) {
      // Reutilizar painel existente, merging function props from callbacksStore
      const callbacks = callbacksStore.value.get(levelData.level) || {}
      existingPanel.props = { ...levelData.props, ...callbacks.functionProps }
      existingPanel.size = calculatePanelSize(levelData.level, levels.length)
      newStack.push(existingPanel)
      continue
    }

    const panelInfo = getPanelInfo(levelData.name)

    if (!panelInfo) {
      // eslint-disable-next-line no-console
      console.error(`[SidePanelManager] Panel "${levelData.name}" not found in registry`)
      continue
    }

    // Recuperar callbacks e options armazenados
    const callbacks = callbacksStore.value.get(levelData.level) || {}

    // Painel efêmero sem entry no callbacksStore = página foi recarregada (F5).
    // Truncar o stack a partir deste nível e navegar para o hash limpo.
    // O painel pai (níveis anteriores) permanece aberto normalmente.
    // Atenção! Níveis após o painel efêmero serão removidos sem chance de reaparecer, pois o hash será reescrito sem eles. Use com cuidado.
    if (panelInfo.ephemeral && !callbacksStore.value.has(levelData.level)) {
      // eslint-disable-next-line no-console
      console.info(
        `[SidePanelManager] Ephemeral panel "${levelData.name}" has no callbacks — removing from stack`,
      )
      const cleanHash = buildHash(newStack)
      await router.replace({ path: route.path, query: route.query, hash: cleanHash })
      return
    }

    const panelEntry = {
      level: levelData.level,
      name: levelData.name,
      props: { ...levelData.props, ...callbacks.functionProps }, // Merge function props back
      size: calculatePanelSize(levelData.level, levels.length),
      title: callbacks.title || panelInfo.title,
      isOpen: true,
      loading: true,
      component: null,
      context: callbacks.context || {},
      callbacks: {
        onClose: callbacks.onClose,
        beforeClose: callbacks.beforeClose,
      },
    }

    // Lazy load do componente
    try {
      const module = await panelInfo.component()
      panelEntry.component = markRaw(module.default)
      panelEntry.loading = false
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(`[SidePanelManager] Failed to load panel: ${levelData.name}`, error)
      panelEntry.loading = false
    }

    newStack.push(panelEntry)
  }

  // Atualizar stack
  panelStack.value = newStack
}

async function closePanelLevel(index) {
  const panel = panelStack.value[index]

  if (!panel) return

  // Ler sempre do callbacksStore (fonte de verdade, atualizado pelo setBeforeClose)
  const callbacks = callbacksStore.value.get(panel.level) || panel.callbacks || {}

  const beforeClose = callbacks.beforeClose

  // Executar beforeClose se existir
  if (beforeClose) {
    try {
      const canClose = await beforeClose()
      if (!canClose) return
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('[SidePanelManager] beforeClose error:', error)
      return
    }
  }

  // Capturar onClose antes de limpar callbacks
  const onCloseCb = callbacks.onClose

  // Remover níveis do index em diante
  const newStack = panelStack.value.slice(0, index)
  const newHash = buildHash(newStack)

  // Limpar callbacks e hooks dos níveis removidos
  for (let i = index; i < panelStack.value.length; i++) {
    callbacksStore.value.delete(panelStack.value[i].level)
  }

  // Navegar primeiro — o hash precisa estar atualizado antes do onClose
  // (consistente com useSidePanel.close(), para que qualquer close() chamado
  // dentro do callback já enxergue o hash correto)
  await router.replace({
    path: route.path,
    query: route.query,
    hash: newHash,
  })

  // Chamar onClose depois da navegação
  if (onCloseCb) {
    try {
      onCloseCb(null)
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('[SidePanelManager] onClose error:', error)
    }
  }
}
</script>

<template>
  <div class="sidepanel-manager">
    <TransitionGroup name="panel" appear>
      <div
        v-for="(panel, index) in panelStack"
        :key="`panel-level-${panel.level}`"
        class="panel-wrapper"
        :style="{ zIndex: 40 + index * 10 }"
        :data-level="index"
      >
        <!-- Dimmer que fecha ao clicar -->
        <div class="panel-dimmer" @click="closePanelLevel(index)"></div>

        <!-- Container do painel que desliza -->
        <div
          class="panel-container"
          :class="`size-${panel.size}`"
          :style="{ '--level-offset': index }"
        >
          <!-- Corpo do painel -->
          <div class="panel-body">
            <!-- Loading state durante lazy load -->
            <div v-if="panel.loading" class="panel-loading">
              <div class="spinner"></div>
            </div>

            <!-- Componente carregado -->
            <PanelLevelProvider v-else :level="panel.level">
              <component :is="panel.component" v-bind="panel.props" />
            </PanelLevelProvider>
          </div>
        </div>
      </div>
    </TransitionGroup>
  </div>
</template>

<style scoped>
.sidepanel-manager {
  position: relative;
  z-index: 40;
}

/* Wrapper fixo para cada nível de painel */
.panel-wrapper {
  position: fixed;
  inset: 0;
  display: flex;

  /* Mobile: alinha ao fundo (sheet) */
  align-items: flex-end;
  justify-content: stretch;
}

@media (min-width: 640px) {
  .panel-wrapper {
    /* Desktop: alinha à direita */
    align-items: stretch;
    justify-content: flex-end;
  }
}

/* Dimmer que cobre tudo atrás e fecha ao clicar */
.panel-dimmer {
  position: fixed;
  inset: 0;
  background: rgba(156, 163, 175, 0.75);
  backdrop-filter: blur(4px);
  z-index: 50;
}

/* Container do painel */
.panel-container {
  position: relative;
  background: white;
  box-shadow: -4px 0 24px rgba(0, 0, 0, 0.15);
  display: flex;
  flex-direction: column;
  z-index: 100;

  /* Mobile: sheet que sobe, largura total, altura reduzida por nível */
  width: 100%;
  height: calc(90vh - var(--level-offset, 0) * 64px);
  border-radius: 16px 16px 0 0;
}

@media (min-width: 640px) {
  /* Desktop: painel lateral, altura total, sem rounded-t, sem offset */
  .panel-container {
    width: auto;
    height: 100vh;
    border-radius: 16px 0 0 16px;
  }

  .panel-container.size-sm {
    width: 520px;
    max-width: 520px;
  }

  .panel-container.size-md {
    width: 690px;
    max-width: 690px;
  }

  .panel-container.size-lg {
    width: 770px;
    max-width: 770px;
  }
}

/* Corpo do painel com scroll */
.panel-body {
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
}

/* Animações - wrapper precisa de transition para o TransitionGroup saber o tempo */
.panel-enter-active,
.panel-leave-active,
.panel-appear-active {
  transition: opacity 0.7s ease;
}

/* Mas forçamos opacity sempre 1 para não ter fade no wrapper */
.panel-enter-from,
.panel-appear-from,
.panel-leave-to {
  opacity: 1 !important;
}

/* Animações - dimmer fade */
.panel-enter-active .panel-dimmer,
.panel-leave-active .panel-dimmer,
.panel-appear-active .panel-dimmer {
  transition: opacity 0.7s ease;
}

.panel-enter-from .panel-dimmer,
.panel-leave-to .panel-dimmer,
.panel-appear-from .panel-dimmer {
  opacity: 0;
}

/* Animações - painel slide */
.panel-enter-active .panel-container,
.panel-leave-active .panel-container,
.panel-appear-active .panel-container {
  transition: transform 0.7s ease;
}

/* Mobile: entra de baixo */
.panel-enter-from .panel-container,
.panel-leave-to .panel-container,
.panel-appear-from .panel-container {
  transform: translateY(100%);
}

/* Desktop: entra da direita */
@media (min-width: 640px) {
  .panel-enter-from .panel-container,
  .panel-leave-to .panel-container,
  .panel-appear-from .panel-container {
    transform: translateX(100%);
  }
}

/* Loading spinner */
.panel-loading {
  display: flex;
  height: 100%;
  align-items: center;
  justify-content: center;
}

.spinner {
  width: 48px;
  height: 48px;
  border: 3px solid #e5e7eb;
  border-top-color: #3b82f6;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}
</style>
